import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { _, core, isArray, isDefined, isObject } from '@icc/shared/helpers';
import { StepComponent, ModalService, DrawService, SizeRangeService, ConfigurationsService } from '@icc/configurator/shared';
import { WindowFacade } from '../+state/window-door.facade';
import { tap, map, withLatestFrom } from 'rxjs/operators';
import { EventBusService, AppConfigFactory, APP_CONFIG, TranslateService, ConfiguratorsDataService } from '@icc/shared';
import { SashesService } from '../modules/sashes/sashes.service';
import { LayoutService } from '../modules/layout/layout.service';
import {
    FormBuilder,
    FormGroup,
    UntypedFormControl,
    ReactiveFormsModule,
    FormsModule,
} from '@angular/forms';
import { UnitConverterService, iccListItem } from '@icc/configurator/ui';
import { Observable } from 'rxjs';
import { SashLayoutPageComponent } from '../sash-layout-page/sash-layout-page.component';
import { BrowserProfilesService } from '../profiles/profiles.service';
import { ThresholdsService } from './thresholds.service';
import { DoorActiveConfiguration, Profile, WindowActiveConfiguration } from '@icc/window-door/types';
import { DimensionsService } from '../modules/dimensions/dimensions.service';
import { SashTypesService } from '../modules/layout/sash-types.service';
import { FillingsService } from '../modules/glazings/fillings.service';
import { StepsService } from '@icc/configurator/shared';
import { IccDrawMathService } from '@icc/draw';
import { IccLayoutsData } from '@icc/shared/types/api/layouts-data';
import { SystemsService } from '../modules/systems/systems.service';
import { UserService } from "@icc/shared/core";

@Component({
    selector: 'icc-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: false
})
export class LayoutComponent extends StepComponent implements OnInit, OnDestroy {
    static stepName = _('WINDOW|Układ');
    static stepIcon = {
        ligature: 'view_compact',
    };

    public configurator = 'window';
    public stepId = 'layout';
    public title = _('WINDOW|Konstrukcja');
    public options = [
        {
            title: _('WINDOW|Edycja konstrukcji'),
            component: SashLayoutPageComponent,
            show: () => this.availableLayoutEdit(),
        },
    ];
    public allowMirrorLayout: boolean = this.config().IccConfig.Configurators.allowMirrorLayout;
    public sashesLayoutEditInOptions: boolean = this.config().IccConfig.Configurators.sashesLayoutEditInOptions;
    public selectedLayoutId: number | null = null;
    mode: 'list' | 'details' = 'list';

    balcony$ = this.windowFacade.balcony$;
    layout$ = this.windowFacade.layout$;

    balcony = this.configurationsService.conf.Current.Balcony;
    canHaveThreshold = false;
    lowThresholds0$ = this.balcony$.pipe(map(balcony => (this.lowThresholds.length > 0 && balcony && this.canHaveThreshold)));
    lowThresholds1$ = this.windowFacade.lowThreshold$.pipe(
        map(lowThreshold => this.lowThresholds.length > 1 && lowThreshold && this.canHaveThreshold)
    );
    selectedLowThresholdId$ = this.windowFacade.configuration$.pipe(
        map(conf => this.profilesService.getUsedThresholdId(conf))
    );

    lowThresholds: Profile[] = [];

    drawOptions = Object.assign({}, this.drawService.options, {
        dimensions: false,
        indexes: false,
    });

    public defaultLayouts: any[] = [];
    private subscriptions: any[] = [];

    isLayoutSelectionDisabled() {
        return !this.defaultLayouts.some(layout => Number(layout.id) !== Number(this.selectedLayoutId));
    }

    fins = new UntypedFormControl('');
    isMonoblockFrames = false;
    isRenoFrames = this.configurationsService.conf.Current.System.type_frame === 'reno';
    isOneFrameProfile = false;
    oneFinWidth = false;
    oneMaxFinWidth = Infinity;
    oneMinFinWidth = 0;
    commonFinWidths: number[] = [];
    commonFinWidth: { finWidth: number; finWidthInterval?: number } = { finWidth: 0 };
    shape = 'rect';
    frameSides: {
        canBeTrimmed: any;
        type: string;
        profile: Profile | null;
        side: {
            side: string;
            sideSimple: string;
            frameEdges: { frameId: number; frameEdgeIndex: number }[];
        };
        id: number;
        finWidth: any;
        finWidths: number[];
        monoblockFrames: Profile[];
        maxFinWidth: any;
        minFinWidth: any;
    }[] = [];
    isCircle =
        !this.configurationsService.conf.Current.hasRoller
        && this.shape !== 'circle'
        && this.isOneFrameProfile;
    monoblock$ = this.windowFacade.monoblock$;
    reno = this.configurationsService.conf.Current.System.type_frame === 'reno';
    mono =
        this.configurationsService.conf.Current.System.type_frame === 'monoblock'
        && this.isMonoblockFrames;
    canChangeDoorSide =
        this.configurationsService.conf.Current.type === 'door';
    doorSide: 'W_L' | 'W_R' | 'Z_L' | 'Z_R' = 'W_L';
    layoutSides = {
        arrWL: [],
        arrWR: [],
        arrZL: [],
        arrZR: []
    }

    hideLayoutWhenUnavailable = false;
    flipSymbols = false;
    canBeTrimmed = false;

    constructor(
        private windowFacade: WindowFacade,
        private configurationsService: ConfigurationsService<'window' | 'door' | 'sliding_door' | 'hs' | 'folding_door'>,
        private configuratorsDataService: ConfiguratorsDataService,
        private systemsService: SystemsService,
        private sashesService: SashesService,
        private layoutService: LayoutService,
        private eventBusService: EventBusService,
        private modalService: ModalService,
        private thresholdsService: ThresholdsService,
        private profilesService: BrowserProfilesService,
        private dimensionsService: DimensionsService,
        public drawService: DrawService,
        private sashTypesService: SashTypesService,
        private fillingsService: FillingsService,
        private stepsService: StepsService,
        private sizeRangeService: SizeRangeService,
        private translateService: TranslateService,
        private userService: UserService,
        @Inject(APP_CONFIG) private config: AppConfigFactory
    ) {
        super();
        if (this.userService.get().flip_symbols || this.config()?.flipSymbol) {
            this.flipSymbols = true;
        }
    }

    ngOnInit() {
        this.hideLayoutWhenUnavailable = Boolean(
            this.config().IccConfig.Configurators.door.hideLayoutWhenUnavailable
        );
        if (
            (!this.config().IccConfig.Configurators.window.defaultListLayoutMode
                || this.configurationsService.conf.Current.layoutHasBeenChosen)
            && this.configurationsService.conf.Current.Layout
            && this.configurationsService.conf.Current.Layout.id
            && this.configurationsService.conf.Current.System
            && !this.configurationsService.conf.Current.System.door_type
        ) {
            this.mode = 'details';
        }
        if (this.sashesService.loadedData) {
            this.init();
        }

        this.subscriptions.push(
            this.eventBusService.subscribeWithoutConfiguration('initializedConfigurator', () =>
                this.init()
            ),
            this.eventBusService.subscribe('changedSashes', () =>
                this.init()
            ),
            this.layout$.subscribe(l => {
                this.allowMirrorLayout = l && l.isMirrorLayoutAvailable && this.config().IccConfig.Configurators.allowMirrorLayout;
                this.selectedLayoutId = l && Number(l.id);
            })
        );

        if (!this.hideLayoutWhenUnavailable) {
            this.layoutService.isDoorSideAvailable(this.layoutSides);
        }
    }

    ngOnDestroy() {
        this.subscriptions.map(el => el.unsubscribe());
    }

    init() {
        this.loadLayouts();
        this.eventBusService.subscribe<any>(
            ['changedBalcony'],
            data => {
                    this.canHaveThreshold = this.thresholdsService.canHaveThreshold(data.activeConfiguration as WindowActiveConfiguration);
            }
        );
        this.canHaveThreshold = this.thresholdsService.canHaveThreshold(this.configurationsService.conf.Current);
        this.lowThresholds = this.profilesService.getFilteredProfiles(
            this.configurationsService.conf.Current,
            'threshold'
        );
        if (this.configurationsService.conf.Current.Sashes.length) {
            const { doorSideWZ, doorSideLR } = this.layoutService.getDoorSides();
            this.doorSide = (doorSideWZ + '_' + doorSideLR) as 'W_L' | 'W_R' | 'Z_L' | 'Z_R';
        }
        this.setFrameSides();
    }


    mirrorLayout() {
        this.configurationsService.conf.Current.Sashes.forEach(sash => {
            this.sashTypesService.mirrorSashType(sash, this.configurationsService.conf.Current);
        })
    }

    /**
     * Ładuje gotowe układy.
     */
    loadLayouts() {
        const blockedSashLayoutVariant: { types: any; options: any; }[] = this.layoutService.getBlockedSashLayoutVariants();
        const handlePosition = this.configurationsService.conf.Current.Sashes?.find((k) => k.type?.handle_position)?.type?.handle_position;
        const conf = this.configurationsService.conf.Current;
        const windowPrices = Object.values(this.configuratorsDataService.data.windowPrices)
            .filter((windowPrice: any) => windowPrice.data);

        const layouts = this.layoutService.getLayouts(conf);
        const layoutsToPrepare = this.hideLayoutWhenUnavailable
            ? layouts
            : layouts.filter(this.filterLayoutsForDoorSide.bind(this));
        const preparedLayouts = layoutsToPrepare
            .map((el: any) => {
                const layoutHandlePosition = conf.type === 'door' && this.hideLayoutWhenUnavailable
                    ? this.layoutService.getDoorLayoutInfo(el, conf)?.handlePosition
                    : handlePosition;
                const isSashLayoutBlocked = this.layoutService.isSashLayoutBlocked(
                    el,
                    blockedSashLayoutVariant,
                    layoutHandlePosition
                );
                const errorMessages: string[] = [];
                const infoBox: {title: string, size: string, system: string, show: boolean} = {
                    title: this.translateService.instant("WINDOW|Układ niedostępny, możliwe opcje"),
                    size: this.translateService.instant("WINDOW|Zmiana wymiarów:"),
                    system: this.translateService.instant("WINDOW|Zmiana systemu"),
                    show: false
                }
                if(this.config().IccConfig.Configurators.roller_shutter.configuratorDimensionalRestriction) {
                    const prices = windowPrices.filter((p: any) =>
                        p.sashes_layouts.flat()
                            .some((s: any) =>
                                el.SashesLayoutsVariant.code.includes(s)
                            )
                    );

                    const result = this.layoutService.getLayoutRestrictionMessage(prices, conf.System.id,  conf.Width, conf.Height);
                    if(result.show) {
                        infoBox.show = true;
                        infoBox.size = result.dimensionMessage !== '' ? infoBox.size + result.dimensionMessage : '';
                        infoBox.system = result.systemMessage !== '' ? infoBox.system + result.systemMessage : '';
                        if(infoBox.size === '' && infoBox.system === '') {
                            infoBox.title = this.translateService.instant("WINDOW|Układ niedostępny");
                        }
                    }
                } else if (Number(el.SashesLayoutsVariant?.size_range_id) && this.config()?.IccConfig.Configurators.window?.filterLayoutsOnSizeRanges) {
                    const sizeRanges = this.sizeRangeService.getSizeRange(el.SashesLayoutsVariant?.size_range_id, conf?.Width, conf?.Height);
                    if (sizeRanges && !sizeRanges?.isValid) {
                        if (sizeRanges?.width) {
                            if (sizeRanges?.width?.minX && sizeRanges?.width?.maxX && sizeRanges?.width?.minX !== sizeRanges?.width?.maxX) {
                                errorMessages.push(Math.abs(conf?.Width - sizeRanges?.width?.minX) < Math.abs(conf?.Width - sizeRanges?.width?.maxX) && sizeRanges?.width?.minX
                                    ? this.translateService.instant("WINDOW|Minimalna szerokość: {size} mm", { size: sizeRanges?.width?.minX })
                                    : this.translateService.instant("WINDOW|Maksymalna szerokość: {size} mm", { size: sizeRanges?.width?.maxX }));
                            } else if (sizeRanges?.width?.minX || sizeRanges?.width?.maxX || sizeRanges?.width?.minX === sizeRanges?.width?.maxX) {
                                this.translateService.instant("WINDOW|Szerokość: {size} mm", { size: sizeRanges?.width?.minX || sizeRanges?.width?.maxX })
                            }
                        } if (sizeRanges?.height) {
                            if (sizeRanges?.height?.maxY && sizeRanges?.height?.minY && sizeRanges?.height?.maxY !== sizeRanges?.height?.minY) {
                                errorMessages.push(Math.abs(conf?.Height - sizeRanges?.height?.minY) < Math.abs(conf?.Height - sizeRanges?.height?.maxY) && sizeRanges?.height?.minY
                                    ? this.translateService.instant("WINDOW|Minimalna wysokość: {size} mm", { size: sizeRanges?.height?.minY })
                                    : this.translateService.instant("WINDOW|Maksymalna wysokość: {size} mm", { size: sizeRanges?.height?.maxY }))
                            } else if (sizeRanges?.height?.maxY || sizeRanges?.height?.minY || sizeRanges?.height?.maxY === sizeRanges?.height?.minY) {
                                this.translateService.instant("WINDOW|Wysokość: {size} mm", { size: sizeRanges?.height?.maxY || sizeRanges?.height?.minY })
                            }
                        }
                    }
                }
                return {
                    layout: el,
                    item: {
                        id: Number(el.SashesLayoutsVariant.id),
                        isMirrorLayoutAvailable: el.SashesLayoutsVariant.allow_mirror_layout,
                        title: el.SashesLayoutsVariant.name,
                        imageUrl: '/files/sasheslayoutsvariant/' +
                            ((this.config().IccConfig.Configurators.window.drawingDefaultOuterView
                                && this.configurationsService.conf.conf !== 'door')
                                || (this.config().IccConfig.Configurators.door.drawingDefaultOuterView
                                    && this.configurationsService.conf.conf === 'door')
                                ? (this.flipSymbols && el.SashesLayoutsVariant.img_out_flipped ? el.SashesLayoutsVariant.img_out_flipped : el.SashesLayoutsVariant.img_out)
                                : (this.flipSymbols && el.SashesLayoutsVariant.img_flipped ? el.SashesLayoutsVariant.img_flipped : el.SashesLayoutsVariant.img)),
                        disabled: errorMessages.length || isSashLayoutBlocked || infoBox.show,
                        errorMessages,
                        infoBox
                    }
                };
            });

        if (this.hideLayoutWhenUnavailable) {
            const availableForSide = (doorSide: 'W_L' | 'W_R' | 'Z_L' | 'Z_R') =>
                preparedLayouts
                    .filter(({ layout, item }) =>
                        !item.disabled && this.filterLayoutsForDoorSide(layout, doorSide, false)
                    )
                    .map(({ layout }) => layout);

            this.layoutSides = {
                arrWL: availableForSide('W_L'),
                arrWR: availableForSide('W_R'),
                arrZL: availableForSide('Z_L'),
                arrZR: availableForSide('Z_R')
            };
        }

        this.defaultLayouts = preparedLayouts
            .filter(({ layout }) =>
                !this.hideLayoutWhenUnavailable || this.filterLayoutsForDoorSide(layout)
            )
            .map(({ item }) => item);

    }

    selectWindowSashesLayout(item: iccListItem) {
        const conf = this.configurationsService.conf.Current;
        const defaultConf = this.configurationsService.conf.Current;
        const chosenGlazing = this.layoutService.isGlazingSame();

        const sashesLayout = this.sashesService.layouts.find(
            l => Number(l.SashesLayoutsVariant.id) === item.id
        );
        if (sashesLayout) {
            this.layoutService.selectLayout(sashesLayout).then(() => {
                this.configurationsService.conf.Current.layoutHasBeenChosen = true;
                if (this.configurationsService.conf.Current.System
                    && !this.configurationsService.conf.Current.System.door_type) {
                    this.mode = 'details';
                }
                this.allowMirrorLayout = sashesLayout.SashesLayoutsVariant?.allow_mirror_layout && this.config().IccConfig.Configurators.allowMirrorLayout;
                if (this.fillingsService.getMatchingFillingsForDoorSashes(this.configurationsService.conf.Current, 'doorLight').length <= 1) {
                    this.stepsService.disable('lightsglazing');
                } else {
                    this.stepsService.enable('lightsglazing');
                }

                if (isDefined(chosenGlazing)) {
                    this.fillingsService.setDecorativePanelInDoorSashes(conf as DoorActiveConfiguration, defaultConf, chosenGlazing, 'doorLight', true);
                }

                this.loadLayouts();
            });
        }
    }

    switchLowThreshold(value: boolean) {
        if (value) {
            this.setLowThreshold();
        } else {
            this.unsetLowThreshold();
        }
    }

    canBeBalcony() {
        return this.thresholdsService.canBeBalcony(this.configurationsService.conf.Current);
    }

    changeBalcony(value: boolean) {
        this.configurationsService.conf.Current.Balcony = value;
        this.thresholdsService.changeBalcony();
    }

    hasThreshold() {
        return this.thresholdsService.hasThreshold(this.configurationsService.conf.Current);
    }

    setLowThreshold() {
        this.thresholdsService.setThreshold(this.configurationsService.conf.Current);
    }

    unsetLowThreshold() {
        this.thresholdsService.unsetThreshold(this.configurationsService.conf.Current);
    }

    changeLowThreshold(id) {
        this.thresholdsService.changeLowThreshold(id, this.configurationsService.conf.Current);
    }

    availableLayoutEdit() {
        return (
            this.configurationsService.conf.Current != null
            && this.configurationsService.conf.Current.Sashes?.length > 0
            && (this.allowMirrorLayout || this.showEditLayoutInOptions())
        );
    }

    showEditLayoutInOptions(): boolean {
        return this.sashesLayoutEditInOptions && this.config().preset === 'b2c';
    }

    editLayout() {
        this.modalService.open({
            pageComponent: SashLayoutPageComponent,
        });
    }

    selectedLowThresholdId() {
        this.profilesService.getUsedThresholdId(this.configurationsService.conf.Current);
    }

    setFrameSides() {
        if (
            !this.configurationsService.conf.Current
            || !this.configurationsService.conf.Current.Frames.length
        ) {
            return;
        }

        const conf = this.configurationsService.conf.Current;
        const sides = this.profilesService.getFrameSidesOnEdge(conf);

        this.isRenoFrames = conf.System.type_frame === 'reno'

        if (this.profilesService.loadedData) {
            this.frameSides = sides.map((side, index) => {
                let profile: Profile | null = null;
                let type = 'std';
                let id = 0;
                let finWidth = null;
                let finWidths: number[] = [];
                let monoblockFrames: Profile[] = [];
                let canBeTrimmed = false;
                const frame = conf.Frames.find(f => f.id === side.frameEdges[0].frameId);
                const frameEdgeIndex = side.frameEdges[0].frameEdgeIndex;
                if (frame && frame.frame && frame.frame[frameEdgeIndex]) {
                    profile = this.profilesService.getProfile(
                        frame.frame[frameEdgeIndex].profileId
                    );
                    if (profile.options.indexOf('renovation_frame') > -1 &&profile && !profile.finNoTrim) {
                        canBeTrimmed = true;
                        this.canBeTrimmed = true;
                    }
                    id = frame.frame[frameEdgeIndex].profileId;
                    finWidth =
                        frame.frame[frameEdgeIndex].finWidth != null && frame.frame[frameEdgeIndex].finWidth >= 0
                            ? frame.frame[frameEdgeIndex].finWidth
                            : profile.finWidth;
                    finWidths = this.profilesService.getFinWidths(profile);
                    monoblockFrames = this.profilesService.getMonoblockFrames(
                        side.sideSimple,
                        conf
                    );

                    if (profile.options && profile.options.indexOf('renovation_frame') > -1) {
                        type = 'reno';
                    } else if (conf.System && conf.System.type_frame === 'monoblock') {
                        type = 'monoblock';
                    }
                }

                this.isRenoFrames = this.isRenoFrames || type === 'reno';
                this.isMonoblockFrames = this.isMonoblockFrames || type === 'monoblock';

                return {
                    type,
                    profile,
                    side,
                    id,
                    finWidth,
                    finWidths,
                    monoblockFrames,
                    canBeTrimmed,
                    maxFinWidth: Math.max(...finWidths),
                    minFinWidth: Math.min(...finWidths),
                };
            });
            let oneFinWidth = true;
            let oneMaxFinWidth = this.frameSides[0].maxFinWidth;
            let oneMinFinWidth = this.frameSides[0].minFinWidth;
            let commonFinWidths: number[] = [];
            const finWidth = this.frameSides[0].finWidth;
            for (const side of this.frameSides) {
                if (side.type === 'reno' && side.canBeTrimmed) {
                    if (side.maxFinWidth < oneMaxFinWidth) {
                        oneMaxFinWidth = side.maxFinWidth;
                    }
                    if (side.minFinWidth > oneMinFinWidth) {
                        oneMinFinWidth = side.minFinWidth;
                    }
                    if (finWidth !== side.finWidth) {
                        oneFinWidth = false;
                    }
                    if (commonFinWidths.length === 0) {
                        commonFinWidths = side.finWidths;
                        this.commonFinWidth = { finWidth: side.finWidth, finWidthInterval: side.profile?.finWidthInterval };
                    } else {
                        commonFinWidths = commonFinWidths.filter(value => side.finWidths.includes(value));
                    }
                }
            }
            this.oneFinWidth = oneFinWidth;
            this.oneMaxFinWidth = oneMaxFinWidth;
            this.oneMinFinWidth = oneMinFinWidth;
            this.commonFinWidths = commonFinWidths;
            this.isOneFrameProfile =
                this.frameSides.map(side => side.id).filter((v, i, a) => a.indexOf(v) === i).length
                === 1;

            if (this.frameSides.length > 0 && oneFinWidth) {
                this.frameSides[0].finWidths = this.frameSides[0].finWidths.filter(
                    (el) => el >= 0
                );
            }
        }
    }

    onChangeRenoDimensions(index: null | number = null) {
        if (
            this.config().IccConfig.Configurators.window.maxFinWidth
            && index != null
            && this.frameSides[index].finWidth > this.frameSides[index].maxFinWidth
        ) {
            this.frameSides[index].finWidth = this.frameSides[index].maxFinWidth;
        }
        if (
            index != null
            && this.frameSides[index].finWidth < this.frameSides[index].minFinWidth
        ) {
            this.frameSides[index].finWidth = this.frameSides[index].minFinWidth;
        }
        this.profilesService.setFrameFinWidths(
            this.configurationsService.conf.Current,
            this.frameSides.map(side => (side.type === 'reno' ? side.finWidth : null))
        );
    }

    onChangeOneFinWidth() {
        if (
            this.config().IccConfig.Configurators.window.maxFinWidth
            && this.commonFinWidth.finWidth > this.oneMaxFinWidth
        ) {
            this.commonFinWidth.finWidth = this.oneMaxFinWidth;
        }
        if (
            this.commonFinWidth.finWidth < this.oneMinFinWidth
        ) {
            this.commonFinWidth.finWidth = this.oneMinFinWidth;
        }
        if (this.commonFinWidths.length && !this.commonFinWidths.includes(this.commonFinWidth.finWidth)) {
            this.commonFinWidth.finWidth = this.commonFinWidths[0];
        }
        this.frameSides.forEach(side => {
            if (side.canBeTrimmed) {
                side.finWidth = this.commonFinWidth.finWidth;
            }
        });
        this.profilesService.setFrameFinWidths(
            this.configurationsService.conf.Current,
            this.frameSides.map(side => (side.type === 'reno' ? side.finWidth : null))
        );
        this.onChangeRenoDimensions();
    }

    changedDoorSide(event: 'W_L' | 'W_R' | 'Z_L' | 'Z_R') {
        this.doorSide = event;
        this.loadLayouts();
        const doorSidesArray = this.doorSide.split('_');
        this.layoutService.selectDefaultLayout(doorSidesArray[0], doorSidesArray[1]);
        this.loadLayouts();
    }

    filterLayoutsForDoorSide(
        layout: any,
        doorSide: 'W_L' | 'W_R' | 'Z_L' | 'Z_R' = this.doorSide,
        showMismatchedVariants = this.config().IccConfig.Configurators.showMismatchedVariants
    ) {
        if (this.configurationsService.conf.Current.type === 'door' && this.canChangeDoorSide) {
            const doorSideWZ =
                (layout.Neighbours && layout.Neighbours.middle.some(s => s.type === 'DOA') || layout.Sashes && layout.Sashes.some(s => s.type === 'DOA'))
                    ? 'Z'
                    : 'W';
            const doorSideLR =
                (layout.Neighbours
                    && layout.Neighbours.middle.some(
                        s => s.type === 'DRA' && s.handle_position === 'R' || s.type === 'DOA' && s.handle_position === 'L'
                    ) || layout.Sashes
                    && layout.Sashes.some(
                        s => s.type === 'DRA' && s.handle_position === 'R' || s.type === 'DOA' && s.handle_position === 'L'
                    ))
                    ? 'L'
                    : 'R';
            return (
                showMismatchedVariants
                || (!doorSide || doorSide === doorSideWZ + '_' + doorSideLR)
            );
        } else {
            return true;
        }
    }

    showDefaultLayoutButton() {
        const conf = this.configurationsService.conf.Current;
        return !conf.Price && this.config().IccConfig.Configurators.roller_shutter.configuratorDimensionalRestriction;
    }

    setDefaultLayout() {
        const conf = this.configurationsService.conf.Current;
        const layoutId = conf.Layout.id;
        const layout = this.layoutService
            .getLayouts(conf)
            .filter(item => this.filterLayoutsForDoorSide(item))
            .find((l: any) => Number(l.SashesLayoutsVariant.id) === Number(layoutId));
        const preparedLayout = this.layoutService.prepareSashesLayout(layout);
        this.layoutService.prepareWindowSashesData(preparedLayout);
    }
}
